import type { ApiClient } from '../../api/client';
import { useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react';
import {
  draftFileName,
  draftNameOf,
  saveDraftFile,
  writeDraftFile,
  type DraftFileHandle,
} from './draft-file';
import type { useDraftHistory } from './useDraftHistory';

/** The writer's account, for keeping drafts in. */
export interface AccountDrafts {
  /** Who's signed in: null for nobody, who keeps none, and undefined until that's known. */
  readonly owner: string | null | undefined;
  readonly client: Pick<
    ApiClient,
    'listDrafts' | 'getDraft' | 'createDraft' | 'updateDraft' | 'deleteDraft'
  >;
  /** Asks the writer to sign in, to save to their account. */
  readonly onSignIn: () => void;
}

/** Where a draft is saved, and what it said when it was. */
export type SavedPlace =
  | {
      readonly kind: 'account';
      readonly id: string;
      readonly name: string;
      readonly text: string;
    }
  | {
      readonly kind: 'file';
      readonly name: string;
      readonly fileName: string;
      readonly text: string;
      /** The file to save to again; null for a file downloaded, or opened without one. */
      readonly handle: DraftFileHandle | null;
    };

type DraftHistory = ReturnType<typeof useDraftHistory>;

export interface SavedDrafts {
  /** Where the draft in the editor is saved; null when it's saved nowhere. */
  readonly place: SavedPlace | null;
  /** Whether the draft has changed since it was saved there. */
  readonly unsaved: boolean;
  /** Whether Save can save it where it is, without asking where. */
  readonly canSaveInPlace: boolean;
  /** Saves the draft where it is, as Save does; false when it has to ask where. */
  readonly saveInPlace: () => Promise<boolean>;
  /** Saves to the account: changes to the draft opened from there, or else, or `copy`, a new one. */
  readonly saveToAccount: (name: string, copy?: boolean) => Promise<SavedPlace>;
  /** Saves a file on this computer; null when the writer closed the picker. */
  readonly saveToComputer: (name: string, fallback: string) => Promise<SavedPlace | null>;
  /** Puts a draft opened from the account in the editor, as one edit that Undo takes back. */
  readonly openFromAccount: (id: string) => Promise<SavedPlace>;
  /** Puts the text of a file in the editor, as one edit that Undo takes back. */
  readonly openFromFile: (
    fileName: string,
    text: string,
    handle: DraftFileHandle | null,
  ) => SavedPlace;
  /** Deletes a draft from the account; the editor keeps its text, saved nowhere now. */
  readonly deleteFromAccount: (id: string) => Promise<void>;
}

const accountKey = (id: string) => `account:${id}`;

/**
 * Keeps track of where the editor's draft is saved, if anywhere, and saves and opens drafts:
 * in the writer's account, or as files on this computer. Each step of the draft's history knows
 * where it was saved, so Undo after opening a draft brings back the one before, and where that
 * was, rather than saving one over the other.
 */
export function useSavedDrafts(history: DraftHistory, account: AccountDrafts | undefined) {
  const [places, setPlaces] = useState<Readonly<Record<string, SavedPlace>>>({});
  const files = useRef(0);
  const place = (history.place === null ? undefined : places[history.place]) ?? null;
  const unsaved = place !== null && place.text !== history.value;
  // The latest, for saves that finish after the writer has carried on.
  const latest = useRef({ history, places });
  useLayoutEffect(() => {
    latest.current = { history, places };
  });

  // An account's drafts are its own: signing out, or in as someone else, lets go of them.
  const owner = account?.owner;
  const knownOwner = useRef(owner);
  useEffect(() => {
    if (owner === undefined || owner === knownOwner.current) return;
    const before = knownOwner.current;
    knownOwner.current = owner;
    if (before === undefined) return;
    setPlaces((current) =>
      Object.fromEntries(Object.entries(current).filter(([, kept]) => kept.kind !== 'account')),
    );
  }, [owner]);

  const keep = useCallback((key: string, saved: SavedPlace) => {
    setPlaces((current) => ({ ...current, [key]: saved }));
  }, []);

  /**
   * Notes that the draft is saved at `key` now. One saved nowhere till now takes its history
   * there with it; one that was saved elsewhere, as when copied, only from here on.
   */
  const moveTo = useCallback((key: string, from: string | null) => {
    const { history, places } = latest.current;
    // The writer may have opened another draft meanwhile.
    if (history.place !== from || from === key) return;
    history.save(key, from === null || places[from] === undefined);
  }, []);

  const client = account?.client;

  const saveToAccount = useCallback(
    async (name: string, copy = false): Promise<SavedPlace> => {
      if (!client) throw new Error('No account to save to');
      const { history, places } = latest.current;
      const from = history.place;
      const current = from === null ? undefined : places[from];
      const text = history.value;
      const draft =
        current?.kind === 'account' && !copy
          ? await client.updateDraft(current.id, { name, text })
          : await client.createDraft({ name, text });
      const saved: SavedPlace = { kind: 'account', id: draft.id, name: draft.name, text };
      keep(accountKey(draft.id), saved);
      moveTo(accountKey(draft.id), from);
      return saved;
    },
    [client, keep, moveTo],
  );

  const saveToComputer = useCallback(
    async (name: string, fallback: string): Promise<SavedPlace | null> => {
      const { history } = latest.current;
      const from = history.place;
      const text = history.value;
      const fileName = draftFileName(name, fallback);
      const handle = await saveDraftFile(fileName, text);
      if (handle === undefined) return null;
      const chosen = handle?.name ?? fileName;
      const saved: SavedPlace = {
        kind: 'file',
        name: draftNameOf(chosen) || fallback,
        fileName: chosen,
        text,
        handle,
      };
      files.current += 1;
      const key = `file:${String(files.current)}`;
      keep(key, saved);
      moveTo(key, from);
      return saved;
    },
    [keep, moveTo],
  );

  const saveInPlace = useCallback(async (): Promise<boolean> => {
    const { history, places } = latest.current;
    const key = history.place;
    const current = key === null ? undefined : places[key];
    if (!key || !current) return false;
    if (current.kind === 'account') {
      if (!client) return false;
      await saveToAccount(current.name);
      return true;
    }
    if (!current.handle) return false;
    const text = history.value;
    await writeDraftFile(current.handle, text);
    keep(key, { ...current, text });
    return true;
  }, [client, keep, saveToAccount]);

  const openFromAccount = useCallback(
    async (id: string): Promise<SavedPlace> => {
      if (!client) throw new Error('No account to open from');
      const draft = await client.getDraft(id);
      const saved: SavedPlace = { kind: 'account', id, name: draft.name, text: draft.text };
      keep(accountKey(id), saved);
      latest.current.history.replace(draft.text, accountKey(id));
      return saved;
    },
    [client, keep],
  );

  const openFromFile = useCallback(
    (fileName: string, text: string, handle: DraftFileHandle | null): SavedPlace => {
      files.current += 1;
      const key = `file:${String(files.current)}`;
      const saved: SavedPlace = {
        kind: 'file',
        name: draftNameOf(fileName),
        fileName,
        text,
        handle,
      };
      keep(key, saved);
      latest.current.history.replace(text, key);
      return saved;
    },
    [keep],
  );

  const deleteFromAccount = useCallback(
    async (id: string) => {
      if (!client) return;
      await client.deleteDraft(id);
      setPlaces((current) =>
        Object.fromEntries(Object.entries(current).filter(([key]) => key !== accountKey(id))),
      );
    },
    [client],
  );

  return {
    place,
    unsaved,
    canSaveInPlace:
      place !== null && (place.kind === 'account' ? client !== undefined : place.handle !== null),
    saveInPlace,
    saveToAccount,
    saveToComputer,
    openFromAccount,
    openFromFile,
    deleteFromAccount,
  } satisfies SavedDrafts;
}
